import {
  $createRangeSelection,
  $getRoot,
  $isElementNode,
  $isTextNode,
  NodeKey,
  RangeSelection,
} from 'lexical';

import {isNil} from 'ramda';

import {TextEditorSelection} from '../../../types/TextEditorApi';
import {$collectEditorNodeRanges, EditorNodeRange} from './collectEditorNodeRanges';

interface ResolvedPoint {
  key: NodeKey;
  offset: number;
  type: 'text' | 'element';
}

type Bias = 'start' | 'end';

const $leafPoint = (index: number, leaf: EditorNodeRange): ResolvedPoint | null => {
  if ($isTextNode(leaf.node)) {
    return {key: leaf.node.getKey(), offset: index - leaf.start, type: 'text'};
  }

  const parent = leaf.node.getParent();

  if (isNil(parent)) {
    return null;
  }

  const childIndex = leaf.node.getIndexWithinParent();

  if (index === leaf.start) {
    return {key: parent.getKey(), offset: childIndex, type: 'element'};
  }

  if (index === leaf.end) {
    return {key: parent.getKey(), offset: childIndex + 1, type: 'element'};
  }

  return null;
};

// At a boundary between two leaves the start of a range belongs to the later leaf and the end to
// the earlier one, so a neighbour outside the range is never selected with it.
const $indexToPoint = (
  index: number,
  leaves: EditorNodeRange[],
  bias: Bias
): ResolvedPoint | null => {
  const containing = leaves.filter((leaf) => leaf.start <= index && index <= leaf.end);

  if (containing.length === 0) {
    const firstBlock = $getRoot().getFirstChild();

    return index === 0 && $isElementNode(firstBlock)
      ? {key: firstBlock.getKey(), offset: 0, type: 'element'}
      : null;
  }

  return $leafPoint(index, bias === 'start' ? containing[containing.length - 1] : containing[0]);
};

/** The range selection for a text-content range, or `null` when a boundary falls inside a mention. */
export function $resolveEditorIndexRange(selection: TextEditorSelection): RangeSelection | null {
  const {leaves} = $collectEditorNodeRanges();
  const anchor = $indexToPoint(selection.index, leaves, 'start');
  const focus = $indexToPoint(selection.index + selection.length, leaves, 'end');

  if (isNil(anchor) || isNil(focus)) {
    return null;
  }

  const range = $createRangeSelection();
  range.anchor.set(anchor.key, anchor.offset, anchor.type);
  range.focus.set(focus.key, focus.offset, focus.type);

  return range;
}
